import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import PageLoading from "components/PageLoading";
import NotificationToast from "components/NotificationToast/NotificationToast";
import BonusRangeTable from "../ComponentsExtractAnalysis/RangeTable/BonusRangeTable";
import BonusDocumentTable from "../ComponentsExtractAnalysis/DocumentTable/BonusDocumentTable";
import GenericSmallTable from "../ComponentsExtractAnalysis/GenericTable/GenericSmallTable";
import ValoresJaSolicitadosTable from "../ComponentsExtractAnalysis/ValoresJaSolicitadosTable/ValoresJaSolicitadosTable";
import DeducaoDocumentTable from "../ComponentsExtractAnalysis/DeducaoTable/DeducaoTable";
import {
  mapExtratoSolicitacaoApiToDeducoes,
  mapExtratoSolicitacaoApiToPreview,
  mapExtratoSolicitacaoApiToValoresSolicitados,
  hasDeducaoAtrasoPreenchida,
} from "../V2/mapExtratoSolicitacaoApi";
import {
  fetchExtratoSindicatoDetalhe,
  confirmarExtratoSindicato,
} from "./extractSindicatoService";
import ModalConfirmacaoExtratoSindicato, {
  type ConcordanciaExtrato,
} from "./ModalConfirmacaoExtratoSindicato/ModalConfirmacaoExtratoSindicato";
import MockModeBanner from "./MockModeBanner";
import type { DadosDeducao } from "../ComponentsExtractAnalysis/DeducaoTable/DeducaoTable";
import type { ExtratoValidacaoSindicato } from "../../../../services/ExtractService";
import { PATHS } from "../../../../routes/paths";
import "../ExtractAnalysis/ExtractAnalysisV2.css";
import "../ComponentsExtractAnalysis/ExtractActions/ExtractActions.css";
import "./ExtractSindicatoDetalhe.css";

const formatBRL = (valor: number | null | undefined): string => {
  if (valor == null) return "R$ 0,00";
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatCNPJ = (cnpj: string | undefined): string => {
  if (!cnpj) return "";
  const digits = cnpj.replace(/\D/g, "");
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const ExtractSindicatoDetalheV2 = () => {
  const { solicitacaoAvaliacaoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state ?? {}) as {
    selectedYear?: number;
    solicitacao?: string;
  };

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [statusNome, setStatusNome] = useState<string | null>(null);
  const [dadosSindicato, setDadosSindicato] = useState({ nome: "", cnpj: "", sigla: "" });
  const [previewData, setPreviewData] = useState<ReturnType<typeof mapExtratoSolicitacaoApiToPreview> | null>(null);
  const [deducaoData, setDeducaoData] = useState<DadosDeducao | null>(null);
  const [valoresSolicitados, setValoresSolicitados] = useState<ReturnType<typeof mapExtratoSolicitacaoApiToValoresSolicitados> | null>(null);
  const [valorLiquidoApoioApi, setValorLiquidoApoioApi] = useState<number | null>(null);
  const [toastData, setToastData] = useState({ show: false, message: "", type: "success" });
  const [showModalConfirmacao, setShowModalConfirmacao] = useState(false);
  const [concordancia, setConcordancia] = useState<ConcordanciaExtrato>("");
  const [enviandoConfirmacao, setEnviandoConfirmacao] = useState(false);
  const [extratoConfirmado, setExtratoConfirmado] = useState(false);
  const [validacaoSindicato, setValidacaoSindicato] = useState<ExtratoValidacaoSindicato | null>(null);

  const solicitacaoLabel = routeState.solicitacao ?? "Solicitação";
  const anoReferencia = routeState.selectedYear ?? new Date().getFullYear();

  useEffect(() => {
    const fetchDetalhe = async () => {
      if (!solicitacaoAvaliacaoId) {
        setErro("Solicitação não informada.");
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const detalhe = await fetchExtratoSindicatoDetalhe(solicitacaoAvaliacaoId);
        setDadosSindicato({
          nome: detalhe.dadosSindicato?.nome ?? "",
          cnpj: detalhe.dadosSindicato?.cnpj ?? "",
          sigla: detalhe.dadosSindicato?.sigla ?? "",
        });
        setPreviewData(mapExtratoSolicitacaoApiToPreview(detalhe));
        setDeducaoData(mapExtratoSolicitacaoApiToDeducoes(detalhe));
        setValoresSolicitados(mapExtratoSolicitacaoApiToValoresSolicitados(detalhe));
        setValorLiquidoApoioApi(detalhe.valorLiquidoApoio ?? null);
        setStatusNome((detalhe as any).status?.nome ?? null);
        setValidacaoSindicato(detalhe.validacaoSindicato ?? null);
      } catch (error) {
        console.error("Erro ao carregar extrato do sindicato:", error);
        setErro("Não foi possível carregar o extrato.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetalhe();
  }, [solicitacaoAvaliacaoId]);

  const handleIrParaEnvioDocumentacao = () => {
    if (!solicitacaoAvaliacaoId) {
      navigate(PATHS.DOC_SHIPPING);
      return;
    }
    const id = Number(solicitacaoAvaliacaoId);
    navigate(PATHS.DOC_SHIPPING_V2, {
      state: {
        selectedYear: anoReferencia,
        item: { id, idQuestionarioResposta: id },
        mode: "view",
        idQuestionarioResposta: id,
      },
    });
  };

  const handleConfirmar = () => {
    setConcordancia("");
    setShowModalConfirmacao(true);
  };

  const handleEnviarConfirmacao = async (concordaComValores: boolean, justificativa?: string) => {
    if (!solicitacaoAvaliacaoId) return;
    setEnviandoConfirmacao(true);
    try {
      const resultado = await confirmarExtratoSindicato(solicitacaoAvaliacaoId, {
        validado: concordaComValores,
        observacaoSindicato: concordaComValores ? undefined : justificativa,
      });
      setShowModalConfirmacao(false);
      if (!resultado.sucesso) {
        setToastData({ show: true, message: resultado.mensagem, type: "error" });
        return;
      }
      setExtratoConfirmado(true);
      if (!concordaComValores && justificativa) {
        setValidacaoSindicato({
          observacaoSindicato: justificativa,
          respondidoSindicatoEm: new Date().toISOString(),
        });
      }
      const avisoMock = resultado.mock ? " (mock)" : "";
      setToastData({
        show: true,
        message: `${resultado.mensagem}${avisoMock}`,
        type: concordaComValores ? "success" : "warning",
      });
      if (concordaComValores) setTimeout(() => navigate(PATHS.EXTRACT_SINDICATO), 2500);
    } catch (error) {
      console.error("Erro ao confirmar extrato:", error);
      setToastData({ show: true, message: "Erro inesperado ao confirmar o extrato.", type: "error" });
    } finally {
      setEnviandoConfirmacao(false);
    }
  };

  if (loading) return <PageLoading />;
  if (erro) return <p className="text-danger">{erro}</p>;

  const numeroSolicitacao = Number(/^\s*(\d+)/.exec(solicitacaoLabel ?? "")?.[1] ?? 1);
  const isSegundaSolicitacaoOuPosterior = numeroSolicitacao >= 2;
  const temCriterioBonus = !!previewData?.criterioBonus?.length;
  const temCriterioBonusExtra = !!previewData?.criterioBonusExtra?.length;
  const temCriterioNaoConforme = [
    ...(previewData?.criterioBonus ?? []),
    ...(previewData?.criterioBonusExtra ?? []),
  ].some((c) => c.conforme === false);

  const dadosResumoApoio = [
    ...(isSegundaSolicitacaoOuPosterior
      ? []
      : [{ label: "VALOR MÍNIMO", valor: formatBRL(previewData?.valorMinimo) }]),
    ...(temCriterioBonus
      ? [{ label: "BÔNUS ALCANÇADO", valor: formatBRL(previewData?.valorBonus) }]
      : []),
    ...(temCriterioBonusExtra
      ? [{ label: "BÔNUS EXTRA ALCANÇADO", valor: formatBRL(previewData?.valorBonusExtra) }]
      : []),
    { label: "VALOR TOTAL DO APOIO", valor: formatBRL(previewData?.valorTotalApoioBruto) },
  ];

  const titleBonus: [string, string, string, string] = ["DOCUMENTO BÔNUS", "CONFORME?", "PERCENTUAL", "VALOR"];
  const titleBonusExtra: [string, string, string, string] = ["DOCUMENTO BÔNUS EXTRA", "CONFORME?", "PERCENTUAL", "VALOR"];

  return (
    <Container className="v2-extract-container py-4">
      <MockModeBanner />

      {/* Cabeçalho */}
      <div className="v2-page-header">
        <h1 className="v2-page-title">EXTRATO</h1>
        <div className="d-flex flex-column align-items-end gap-1">
          <div
            className="merit-voltar-seta"
            onClick={() => navigate(PATHS.EXTRACT_SINDICATO)}
            title="Voltar para a lista"
          >
            <FaArrowLeft />
          </div>
          <span className="v2-solicitacao-ref">
            {solicitacaoLabel} - {anoReferencia}
          </span>
        </div>
      </div>

      {/* MOTIVO DA CONTESTAÇÃO */}
      {validacaoSindicato && (
        <div className="v2-card v2-contestacao-card">
          <div className="v2-card-body">
            <div className="v2-card-section-title">MOTIVO DA CONTESTAÇÃO DO EXTRATO</div>
            <Form.Control
              as="textarea"
              rows={4}
              value={validacaoSindicato.observacaoSindicato}
              disabled
              className="v2-contestacao-textarea"
            />
            <div className="v2-contestacao-data">
              {new Date(validacaoSindicato.respondidoSindicatoEm).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).replace(",", " -")}
            </div>
          </div>
        </div>
      )}

      {/* DADOS DO SINDICATO */}
      <div className="v2-card">
        <div className="v2-card-body">
          <div className="v2-card-section-title">DADOS DO SINDICATO</div>
          <div className="mb-3">
            <span className="v2-field-label">NOME DO SINDICATO</span>
            <Form.Control type="text" value={dadosSindicato.nome} disabled />
          </div>
          <Row>
            <Col md={6} className="mb-2">
              <span className="v2-field-label">CNPJ DO SINDICATO</span>
              <Form.Control type="text" value={formatCNPJ(dadosSindicato.cnpj)} disabled />
            </Col>
            <Col md={6} className="mb-2">
              <span className="v2-field-label">SIGLA</span>
              <Form.Control type="text" value={dadosSindicato.sigla} disabled />
            </Col>
          </Row>
        </div>
      </div>

      {/* VALOR DA RECEITA */}
      <div className="v2-card">
        <div className="v2-card-body">
          <div className="v2-card-section-title">VALOR DA RECEITA</div>
          <Row>
            <Col md={6}>
              <span className="v2-field-label">RECEITA OPERACIONAL</span>
              <Form.Control type="text" value={formatBRL(previewData?.receitaOperacional)} disabled />
            </Col>
          </Row>
        </div>
      </div>

      {/* FAIXA */}
      {!isSegundaSolicitacaoOuPosterior && (
        <div className="v2-card">
          <div className="v2-card-body v2-faixa-body">
            <BonusRangeTable
              tituloFaixa={previewData?.faixaReceitaBonus?.titulo}
              receitaMin={previewData?.faixaReceitaBonus?.receitaMin}
              receitaMax={previewData?.faixaReceitaBonus?.receitaMax}
              valorMinimo={previewData?.faixaReceitaBonus?.valorMinimo}
              valorBonusPotencial={previewData?.faixaReceitaBonus?.valorBonusPotencial}
              valorMaximoPotencial={previewData?.faixaReceitaBonus?.valorMaximoPotencial}
            />
          </div>
        </div>
      )}

      {temCriterioNaoConforme && (
        <div className="v2-alert-nao-conforme" role="alert">
          <span className="v2-alert-nao-conforme-icon" aria-hidden="true">!</span>
          <div className="v2-alert-nao-conforme-content">
            <p className="v2-alert-nao-conforme-title">
              Alguns critérios foram avaliados como Não Conforme
            </p>
            <p className="v2-alert-nao-conforme-text">
              Para entender o resultado da análise e consultar as justificativas
              registradas pela FIEMG, acesse{" "}
              <span
                className="v2-alert-nao-conforme-link"
                role="link"
                tabIndex={0}
                onClick={handleIrParaEnvioDocumentacao}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleIrParaEnvioDocumentacao();
                  }
                }}
              >
                Documentação → Envio
              </span>
            </p>
          </div>
        </div>
      )}

      {/* BÔNUS */}
      <div className="v2-card v2-bonus-section">
        <div className="v2-card-body">
          <div className="v2-card-section-title">BÔNUS</div>
          {!!previewData?.criterioBonus?.length && (
            <BonusDocumentTable data={previewData.criterioBonus} title={titleBonus} />
          )}
          {!!previewData?.criterioBonusExtra?.length && (
            <BonusDocumentTable data={previewData.criterioBonusExtra} title={titleBonusExtra} />
          )}
          <GenericSmallTable dados={dadosResumoApoio} corValor="verde" />
        </div>
      </div>

      {/* DEDUÇÃO */}
      {!isSegundaSolicitacaoOuPosterior && (
        <div className="v2-card v2-deducao-section">
          <div className="v2-card-body">
            <div className="v2-card-section-title">DEDUÇÃO</div>
            <DeducaoDocumentTable
              atrasoDocs={
                hasDeducaoAtrasoPreenchida(deducaoData?.deducaoAtraso) &&
                deducaoData?.deducaoAtraso?.deduzir !== false
                  ? [deducaoData!.deducaoAtraso]
                  : []
              }
              titulosEmAberto={(deducaoData?.deducaoTitulos ?? []).filter(
                (t) => t.deduzir !== false,
              )}
              outrasDeducoes={(deducaoData?.deducaoOutros ?? []).filter(
                (o) => o.deduzir !== false,
              )}
              totalBrutoBonus={formatBRL(previewData?.valorTotalApoioBruto)}
              disabled
              hideControls
              initialTotalLiquido={valorLiquidoApoioApi ?? undefined}
            />
          </div>
        </div>
      )}

      {/* VALORES JÁ SOLICITADOS */}
      {valoresSolicitados && (
        <div className="v2-card v2-valores-section">
          <div className="v2-card-body">
            <div className="v2-card-section-title">VALORES JÁ SOLICITADOS</div>
            <ValoresJaSolicitadosTable
              itens={valoresSolicitados.itens}
              total={valoresSolicitados.total}
              valorLiquidoApoio={valoresSolicitados.valorLiquidoApoio}
              hideLiquidoApoio
            />
          </div>
        </div>
      )}

      {/* Ação */}
      {statusNome?.replace(/\s/g, "") === "ExtratoEnviado" && !extratoConfirmado && (
        <div className="v2-actions-footer">
          <div className="save-submit-buttons d-flex gap-2">
            <Button
              variant="success"
              className="sucess-btn"
              onClick={handleConfirmar}
              disabled={enviandoConfirmacao}
            >
              AVALIAR EXTRATO
            </Button>
          </div>
        </div>
      )}

      <ModalConfirmacaoExtratoSindicato
        show={showModalConfirmacao}
        onClose={() => { if (!enviandoConfirmacao) setShowModalConfirmacao(false); }}
        onEnviar={handleEnviarConfirmacao}
        enviando={enviandoConfirmacao}
        concordancia={concordancia}
        onConcordanciaChange={setConcordancia}
      />

      <NotificationToast
        show={toastData.show}
        message={toastData.message}
        type={toastData.type}
        onClose={() => setToastData((prev) => ({ ...prev, show: false }))}
      />
    </Container>
  );
};

export default ExtractSindicatoDetalheV2;
